import React, { useMemo } from 'react';
import { RotateCcw, Plus, X } from 'lucide-react';

export const SmartDiffInput = ({
  label,
  originalValue,
  currentValue,
  onChange,
  multiline = false,
  placeholder = '',
  className = '',
  inputClassName = '',
  hideLabel = false,
  disabled = false,
}) => {
  const hasBaseline = originalValue !== undefined && originalValue !== null;
  const normOriginal = String(originalValue || '').trim();
  const normCurrent = String(currentValue || '').trim();
  const isModified = hasBaseline && normOriginal !== normCurrent;

  const baseInputClass = isModified
    ? 'border-amber-300 bg-amber-50/50'
    : 'border-slate-200 bg-white';

  const defaultClasses = `w-full border rounded-lg px-3 py-2 text-sm transition-all duration-200 focus:ring-2 focus:ring-indigo-100 outline-none ${baseInputClass}`;

  const roleCardHighlight = isModified ? 'bg-amber-50/50 ring-1 ring-amber-200 rounded px-1 -mx-1' : '';

  const effectiveInputClass = `${inputClassName ? `${inputClassName} ${roleCardHighlight}` : defaultClasses} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`;

  return (
    <div className={`space-y-1.5 ${className}`}>
      {!hideLabel && (
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
          {isModified && (
            <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-100 font-medium">
              Modifié
            </span>
          )}
        </div>
      )}

      {isModified && normOriginal && (
        <div className="bg-amber-50/50 border border-amber-100 dashed rounded-lg p-2 text-xs text-slate-600 mb-1">
          <span className="font-semibold text-amber-700 mr-1 uppercase text-[10px]">Avant:</span>
          {originalValue}
        </div>
      )}

      {multiline ? (
        <textarea
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          className={effectiveInputClass}
          rows={3}
          placeholder={placeholder}
          disabled={disabled}
        />
      ) : (
        <input
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          className={effectiveInputClass}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export const SmartDiffList = ({
  label,
  originalList = [],
  currentList = [],
  onChange,
  disabled = false,
}) => {
  const hasBaseline = Array.isArray(originalList);
  const safeOriginal = hasBaseline ? originalList : [];

  const originalSet = useMemo(() => new Set(safeOriginal.map((i) => i.trim()).filter(Boolean)), [safeOriginal]);
  const currentSet = useMemo(() => new Set(currentList.map((i) => i.trim()).filter(Boolean)), [currentList]);

  const addedItems = hasBaseline ? currentList.filter((item) => item.trim() && !originalSet.has(item.trim())) : [];
  const removedItems = hasBaseline ? safeOriginal.filter((item) => item.trim() && !currentSet.has(item.trim())) : [];

  const handleItemChange = (index, newValue) => {
    const newList = [...currentList];
    newList[index] = newValue;
    onChange(newList);
  };

  const handleRemove = (index) => {
    if (disabled) return;
    const newList = [...currentList];
    newList.splice(index, 1);
    onChange(newList);
  };

  const handleAdd = () => {
    if (disabled) return;
    onChange([...currentList, '']);
  };

  const handleRestore = (itemValue) => {
    if (disabled) return;
    onChange([...currentList, itemValue]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
        {hasBaseline && (
          <div className="flex gap-2 text-[10px]">
            {addedItems.length > 0 && <span className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">+{addedItems.length} Ajout</span>}
            {removedItems.length > 0 && <span className="text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-100">-{removedItems.length} Retrait</span>}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-2 shadow-sm space-y-2">
        {hasBaseline && removedItems.length > 0 && (
          <div className="space-y-1 pb-2 border-b border-slate-100 mb-2">
            <p className="text-[10px] text-slate-400 font-semibold uppercase px-1">Supprimés</p>
            {removedItems.map((item, idx) => (
              <div key={`removed-${idx}`} className="flex items-center justify-between group px-2 py-1.5 bg-red-50/50 rounded-lg border border-red-100/50">
                <span className="text-sm text-red-400 line-through decoration-red-300">{item}</span>
                {!disabled && (
                  <button
                    onClick={() => handleRestore(item)}
                    className="text-emerald-600 hover:text-emerald-700 bg-white border border-emerald-200 hover:bg-emerald-50 p-1 rounded-md transition-all shadow-sm opacity-0 group-hover:opacity-100"
                    title="Restaurer"
                    disabled={disabled}
                  >
                    <RotateCcw size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          {currentList.map((item, idx) => {
            const isNew = hasBaseline && !originalSet.has(item.trim()) && item.trim() !== '';
            return (
              <div key={`item-${idx}`} className={`flex items-center gap-2 transition-all duration-300 ${isNew ? 'pl-1' : ''}`}>
                {isNew && <div className="w-1 h-8 bg-emerald-400 rounded-full"></div>}
                <div className={`flex-1 flex items-center gap-2 border rounded-lg px-1 py-1 focus-within:ring-2 focus-within:ring-indigo-100 transition-all ${isNew ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'}`}>
                  <input
                    value={item}
                    onChange={(e) => handleItemChange(idx, e.target.value)}
                    className="flex-1 bg-transparent border-none outline-none text-sm px-2 py-1 text-slate-700 placeholder-slate-400"
                    placeholder="Nouvel élément..."
                    disabled={disabled}
                  />
                  {!disabled && (
                    <button
                      onClick={() => handleRemove(idx)}
                      className="text-slate-300 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!disabled && (
          <div className="pt-1 flex justify-end">
            <button
              onClick={handleAdd}
              className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Plus size={14} /> Ajouter
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default {
  SmartDiffInput,
  SmartDiffList,
};
